import { useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";

const PARTICLE_COUNT = 80;
const COLORS = ["#ff6b9d", "#c084fc", "#7dd3fc", "#fbbf24", "#6ee7b7", "#f472b6", "#FFD700"];

interface ConfettiProps {
  position: [number, number, number];
  // Taille des particules. La valeur d'origine (0.08 unité) était calée sur la
  // caméra de course ; sur le podium, vu de 13+ unités, ça fait 1 à 2 pixels,
  // donc rien de visible à l'écran.
  size?: number;
  // Rayon de dispersion du nuage. VOLONTAIREMENT découplé de `size` : tout
  // multiplier par le même facteur envoyait les particules à ±24 unités et
  // jusqu'à 40 de haut, c'est-à-dire entièrement hors cadre (constaté en capture).
  spread?: number;
}

// Confettis 3D autour du winner
export function Confetti({ position, size = 1, spread = 1 }: ConfettiProps) {
  const meshRef = useRef<THREE.InstancedMesh>(null!);

  // Données par particule : offset, vitesse, phase
  const particles = useMemo(() => {
    return Array.from({ length: PARTICLE_COUNT }, () => ({
      offset: new THREE.Vector3(
        (Math.random() - 0.5) * 6,
        Math.random() * 5,
        (Math.random() - 0.5) * 6
      ),
      velocity: new THREE.Vector3(
        (Math.random() - 0.5) * 0.5,
        -0.5 - Math.random() * 1.5,
        (Math.random() - 0.5) * 0.5
      ),
      phase: Math.random() * Math.PI * 2,
      rotSpeed: (Math.random() - 0.5) * 4,
    }));
  }, []);

  // Couleurs
  const colors = useMemo(() => {
    const arr = new Float32Array(PARTICLE_COUNT * 3);
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      const c = new THREE.Color(COLORS[i % COLORS.length]);
      arr[i * 3] = c.r;
      arr[i * 3 + 1] = c.g;
      arr[i * 3 + 2] = c.b;
    }
    return arr;
  }, []);

  const dummy = useMemo(() => new THREE.Object3D(), []);

  useFrame(({ clock }) => {
    if (!meshRef.current) return;
    const t = clock.getElapsedTime();

    for (let i = 0; i < PARTICLE_COUNT; i++) {
      const p = particles[i];
      // Cycle vertical : remonter quand trop bas
      const cycleY = ((p.offset.y + p.velocity.y * t) % 6 + 6) % 6;

      dummy.position.set(
        position[0] + (p.offset.x + Math.sin(t * 0.5 + p.phase) * 0.5) * spread,
        position[1] + (cycleY - 1) * spread,
        position[2] + (p.offset.z + Math.cos(t * 0.7 + p.phase) * 0.5) * spread
      );
      dummy.rotation.set(
        t * p.rotSpeed,
        t * p.rotSpeed * 0.7,
        t * p.rotSpeed * 0.3
      );
      dummy.scale.setScalar((0.08 + Math.sin(t * 2 + p.phase) * 0.03) * size);
      dummy.updateMatrix();
      meshRef.current.setMatrixAt(i, dummy.matrix);
    }
    meshRef.current.instanceMatrix.needsUpdate = true;
  });

  return (
    // frustumCulled={false} est OBLIGATOIRE ici : les positions sont cuites dans
    // les matrices d'instance, donc le mesh lui-même reste à l'origine du monde
    // avec le bounding sphere d'un simple plan 1x1. Dès que les particules sont
    // loin de l'origine — c'est le cas sur le podium, posé à PODIUM_STAGE_X —
    // three considère le mesh hors champ et ne dessine RIEN. C'était la vraie
    // raison de l'absence de confettis sur le podium, pas leur taille.
    <instancedMesh
      ref={meshRef}
      args={[undefined, undefined, PARTICLE_COUNT]}
      frustumCulled={false}
    >
      <planeGeometry args={[1, 1]} />
      <meshBasicMaterial vertexColors side={THREE.DoubleSide} />
      <instancedBufferAttribute
        attach="geometry-attributes-color"
        args={[colors, 3]}
      />
    </instancedMesh>
  );
}
